import { Link } from "react-router-dom";
import { Lock, Search, Star } from "lucide-react";
import { localeSort } from "@/i18n";

export const DEFAULT_FILTERS = {
  league: "", state: "", club: "", search: "", status: "watching", position: "", my_list: false, card: "", rc_card: false,
  hype_min: "", hype_max: "", delta_min: "", age_min: "", age_max: "", sort: "hype",
};
export const PREMIUM_KEYS = ["position", "hype_min", "hype_max", "delta_min", "age_min", "age_max"];

export function activeFilterLabels(filters, t) {
  const out = [];
  if (filters.search) out.push(`${t("filters.search")}: “${filters.search}”`);
  if (filters.league) out.push(`${t("filters.league")}: ${filters.league}`);
  if (filters.club) out.push(`${t("filters.club")}: ${filters.club}`);
  if (filters.status !== "watching") out.push(`${t("filters.status")}: ${t(`filters.status_${filters.status}`)}`);
  if (filters.state) out.push(`${t("filters.state")}: ${t(`state.${filters.state}`)}`);
  if (filters.rc_card) out.push(t("filters.with_rc_card"));
  if (filters.my_list) out.push(t("filters.my_list"));
  if (filters.position) out.push(`${t("filters.position")}: ${t(`pos.${filters.position}`)}`);
  if (filters.hype_min !== "" || filters.hype_max !== "") out.push(`HYPE ${filters.hype_min || 0}–${filters.hype_max || 100}`);
  if (filters.delta_min !== "") {
    out.push(filters.delta_min === "1" ? t("filters.risers_only")
      : t(filters.delta_min === "10" ? "filters.delta_strong" : "filters.delta_rising"));
  }
  if (filters.age_min !== "" || filters.age_max !== "") out.push(`${t("filters.age")} ${filters.age_min || "·"}–${filters.age_max || "·"}`);
  if (filters.sort !== "hype") out.push(`${t("filters.sort")}: ${t(`filters.sort_${filters.sort}`)}`);
  return out;
}

const selectCls = "w-full border border-[var(--wl-line)] bg-[var(--wl-panel)] px-2.5 py-1.5 wl-mono text-[10.5px] text-[var(--wl-text)] outline-none transition-colors focus:border-[var(--wl-accent)]";
const numCls = "w-full border border-[var(--wl-line)] bg-[var(--wl-panel)] px-2 py-1.5 wl-mono text-[10.5px] text-[var(--wl-text)] outline-none focus:border-[var(--wl-accent)]";

function LockedControl({ label, reason, testid }) {
  return (
    <Link to="/pricing" data-testid={testid} title={reason}
      className="wl-locked flex w-full items-center gap-1.5 border border-dashed border-[var(--wl-line)] px-2.5 py-1.5 wl-mono text-[10px] uppercase tracking-[.04em] text-[var(--wl-muted)] transition-colors hover:border-[var(--wl-accent)] hover:text-[var(--wl-accent)]">
      <Lock className="h-3 w-3 shrink-0 text-[var(--wl-accent)]" />
      <span className="truncate">{label}</span>
      <span className="ml-auto hidden truncate text-[8.5px] normal-case tracking-normal opacity-70 lg:inline">{reason}</span>
    </Link>
  );
}

export function WatchlistFilters({ filters, setFilters, premium, me, clubs, rcCount, listLimit, atFreeLimit, onMyList, t, lang }) {
  const set = (patch) => setFilters({ ...filters, ...patch });
  return (
    <div className="grid grid-cols-2 gap-2 lg:grid-cols-6" data-testid="watchlist-filters">
      <div className="relative col-span-2">
        <Search className="pointer-events-none absolute left-2.5 top-2 h-3.5 w-3.5 text-[var(--wl-muted)]" />
        <input data-testid="watchlist-search-input" className={`${selectCls} pl-8`} placeholder={t("filters.search")} value={filters.search} onChange={(e) => set({ search: e.target.value })} />
      </div>
      <select data-testid="watchlist-filter-status-select" className={selectCls} value={filters.status} onChange={(e) => set({ status: e.target.value })}>
        {["watching", "graduated", "all"].map((s) => <option key={s} value={s}>{t("filters.status")} — {t(`filters.status_${s}`)}</option>)}
      </select>
      <select data-testid="watchlist-filter-club-select" className={selectCls} value={filters.club} onChange={(e) => set({ club: e.target.value })}>
        <option value="">{t("filters.club")} — {t("filters.any")}</option>
        {localeSort(clubs || [], lang).map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <select data-testid="watchlist-filter-milestone-select" className={selectCls} value={filters.state} onChange={(e) => set({ state: e.target.value })}>
        <option value="">{t("filters.state")} — {t("filters.any")}</option>
        {["called_up", "debuted", "started", "scored", "none"].map((s) => <option key={s} value={s}>{t(`state.${s}`)}</option>)}
      </select>
      <label className="flex items-center gap-1.5 border border-[var(--wl-line)] px-3 py-1.5 wl-mono text-[10px] uppercase tracking-[.04em] text-[var(--wl-muted)] hover:text-[var(--wl-accent)]" data-testid="watchlist-rc-card-filter" title={t("filters.with_rc_card")}>
        <input type="checkbox" checked={filters.rc_card} onChange={(e) => set({ rc_card: e.target.checked })} data-testid="watchlist-rc-card-checkbox" />
        <span className="wl-rc-badge" title={t("filters.with_rc_card")}>RC</span>
        <b className="ml-auto text-[var(--wl-text)]" data-testid="watchlist-rc-card-count">{rcCount ?? "—"}</b>
      </label>

      {/* PREMIUM — shown locked, never hidden */}
      {premium ? (
        <select data-testid="watchlist-filter-position-select" className={selectCls} value={filters.position} onChange={(e) => set({ position: e.target.value })}>
          <option value="">{t("filters.position")} — {t("filters.any")}</option>
          {localeSort(["GK", "DEF", "MID", "FWD"], lang, (pg) => t(`pos.${pg}`)).map((pg) => <option key={pg} value={pg}>{t(`pos.${pg}`)}</option>)}
        </select>
      ) : <LockedControl label={t("filters.position")} reason={t("filters.lock_position")} testid="watchlist-filter-position-locked" />}
      {premium ? (
        <div className="flex items-center gap-1" data-testid="watchlist-filter-hype-range">
          <span className="wl-mono text-[9px] uppercase text-[var(--wl-muted)]">HYPE</span>
          <input type="number" min="0" max="100" placeholder="0" className={numCls} value={filters.hype_min} onChange={(e) => set({ hype_min: e.target.value })} data-testid="watchlist-filter-hype-min" />
          <input type="number" min="0" max="100" placeholder="100" className={numCls} value={filters.hype_max} onChange={(e) => set({ hype_max: e.target.value })} data-testid="watchlist-filter-hype-max" />
        </div>
      ) : <LockedControl label="HYPE" reason={t("filters.lock_hype")} testid="watchlist-filter-hype-locked" />}
      {premium ? (
        <select data-testid="watchlist-filter-delta-select" className={selectCls} value={filters.delta_min} onChange={(e) => set({ delta_min: e.target.value })}>
          <option value="">{t("filters.delta")} — {t("filters.any")}</option>
          <option value="1">{t("filters.risers_only")}</option>
          <option value="5">{t("filters.delta_rising")}</option>
          <option value="10">{t("filters.delta_strong")}</option>
        </select>
      ) : <LockedControl label={t("filters.delta")} reason={t("filters.lock_delta")} testid="watchlist-filter-delta-locked" />}
      {premium ? (
        <div className="flex items-center gap-1" data-testid="watchlist-filter-age-range">
          <span className="wl-mono text-[9px] uppercase text-[var(--wl-muted)]">{t("filters.age")}</span>
          <input type="number" min="14" max="45" placeholder="14" className={numCls} value={filters.age_min} onChange={(e) => set({ age_min: e.target.value })} data-testid="watchlist-filter-age-min" />
          <input type="number" min="14" max="45" placeholder="45" className={numCls} value={filters.age_max} onChange={(e) => set({ age_max: e.target.value })} data-testid="watchlist-filter-age-max" />
        </div>
      ) : <LockedControl label={t("filters.age")} reason={t("filters.lock_age")} testid="watchlist-filter-age-locked" />}
      {premium ? (
        <select data-testid="watchlist-sort-select" className={selectCls} value={filters.sort} onChange={(e) => set({ sort: e.target.value })}>
          {["hype", "riser", "recent_milestone"].map((s) => <option key={s} value={s}>{t("filters.sort")} — {t(`filters.sort_${s}`)}</option>)}
        </select>
      ) : <LockedControl label={`${t("filters.sort")} · ${t("filters.sort_hype")}`} reason={t("filters.lock_sort")} testid="watchlist-sort-locked" />}

      <div className="col-span-2 flex gap-2 lg:col-span-6">
        <button data-testid="watchlist-my-list-toggle" onClick={onMyList}
          className={`flex shrink-0 items-center gap-1.5 border px-3 py-1.5 wl-mono text-[10px] uppercase tracking-[.04em] transition-colors ${filters.my_list ? "border-[var(--wl-accent)] bg-[var(--wl-panel2)] text-[var(--wl-accent)]" : "border-[var(--wl-line)] text-[var(--wl-muted)] hover:text-[var(--wl-accent)]"}`}>
          <Star className={`h-3.5 w-3.5 ${filters.my_list ? "fill-[var(--wl-accent)]" : ""}`} />
          <span className="hidden sm:inline">{t("filters.my_list")}</span>
          {me && listLimit.limit !== null && <b className={atFreeLimit ? "text-[var(--wl-accent)]" : "text-[var(--wl-muted)]"} data-testid="my-list-quota">{listLimit.used}/{listLimit.limit}</b>}
        </button>
        <button data-testid="watchlist-reset-filters" onClick={() => setFilters({ ...DEFAULT_FILTERS })}
          className="ml-auto shrink-0 border border-[var(--wl-line)] px-3 py-1.5 wl-mono text-[10px] uppercase tracking-[.04em] text-[var(--wl-muted)] transition-colors hover:border-[var(--wl-accent)] hover:text-[var(--wl-accent)]">
          {t("filters.reset")}
        </button>
      </div>
    </div>
  );
}
